/**
 * §36.1 — the player changes their own name and avatar, in the lobby.
 *
 * The steps are onboarding's steps, imported rather than reimplemented, so this
 * screen offers exactly what onboarding offers and cannot drift from it.
 *
 * The email is shown and cannot be changed. That is not a UI preference: there
 * is no field for it on the wire and no parameter for it on the server, because
 * an address is proved and never asserted, and this call carries a profile
 * token rather than a proven sign-in. It is *shown* rather than hidden because
 * a player has to be able to see which identity they are editing.
 */

import { useState } from "react";
import { Button } from "@tq/ui";
import { api, ApiRequestError } from "../api.js";
import { AnimalStep, ChoiceCard, Field, PhotoStep } from "./onboarding-steps.js";

type Step = "details" | "consent" | "photo" | "animal";

export interface EditProfileProps {
  sessionId: string;
  /** The address on the profile. Displayed, never sent. */
  email: string | null;
  fullName: string;
  onDone: (identity: { displayName: string; photoUrl: string | null }) => void;
  onCancel: () => void;
}

export function EditProfile({
  sessionId,
  email,
  fullName: initialFullName,
  onDone,
  onCancel,
}: EditProfileProps): React.ReactElement {
  const [step, setStep] = useState<Step>("details");
  const [fullName, setFullName] = useState(initialFullName);
  const [photo, setPhoto] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const nameValid = fullName.trim().length > 0;

  const save = async (avatar: {
    avatarConsent?: boolean;
    photo?: string | null;
    avatarAnimal?: string | null;
  }) => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.updateProfile({
        sessionId,
        // Sent on every path: the player may have edited the name and then gone
        // on to change their picture, and both are one save.
        fullName: fullName.trim(),
        ...avatar,
      });
      onDone(result);
    } catch (cause) {
      setError(
        cause instanceof ApiRequestError ? cause.message : "That could not be saved. Try again.",
      );
      setBusy(false);
    }
  };

  return (
    <div className="tq-card" style={{ gap: 14 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>
          {step === "details" ? "Your details" : step === "consent" ? "Your look" : "Your picture"}
        </h2>
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={busy}>
          Close
        </Button>
      </div>

      {error && (
        <div role="alert" className="tq-alert">
          {error}
        </div>
      )}

      {step === "details" && (
        <>
          <Field label="Full name" required>
            <input
              className="tq-answer-input"
              type="text"
              autoComplete="name"
              maxLength={120}
              autoFocus
              value={fullName}
              onChange={(event) => setFullName(event.target.value)}
            />
            <p style={{ margin: "6px 2px 0", fontSize: 12, color: "var(--muted)" }}>
              The room sees a short version of this.
            </p>
          </Field>

          {/* `required` even though it cannot be edited: the address is neither
              optional nor removable, and labelling it "(optional)" would read as
              though it could be dropped. */}
          <Field label="Email address" required>
            {/* Shown, not asked — the same treatment onboarding gives a proved
                address. There is no input here because there is no field for one
                on the wire: see `editProfileSchema`. */}
            <p
              style={{
                margin: 0,
                display: "flex",
                alignItems: "center",
                gap: 9,
                padding: "12px 14px",
                minHeight: "var(--touch-target)",
                boxSizing: "border-box",
                borderRadius: "var(--radius)",
                background: "rgba(142,224,107,.12)",
                border: "2px solid rgba(142,224,107,.55)",
                color: "#fff",
                fontSize: 15,
                fontWeight: 600,
                // A long address must not push the tick off the chip.
                overflowWrap: "anywhere",
              }}
            >
              <span aria-hidden="true" style={{ color: "var(--green)", fontWeight: 900 }}>
                ✓
              </span>
              {email ?? "—"}
              <span className="tq-visually-hidden"> (cannot be changed)</span>
            </p>
            <p style={{ margin: "6px 2px 0", fontSize: 12, color: "var(--muted)" }}>
              This cannot be changed here.
            </p>
          </Field>

          <Button
            variant="primary"
            size="lg"
            disabled={!nameValid || busy}
            onClick={() => void save({})}
            style={{ width: "100%" }}
          >
            {busy ? "Saving…" : "Save name"}
          </Button>
          <Button
            variant="ghost"
            size="lg"
            disabled={busy}
            onClick={() => setStep("consent")}
            style={{ width: "100%" }}
          >
            Change my picture
          </Button>
        </>
      )}

      {/* §36.4 — consent before any photo is requested, the same way round as
          onboarding asks it. Two appealing options, not an action and a
          penalty. */}
      {step === "consent" && (
        <>
          <ChoiceCard
            emoji="🎨"
            title="Make one from my photo"
            body="You get a street-art cartoon of yourself, drawn in the game's style."
            note="Your photo is sent to an outside AI service to draw the avatar. It is never shown to other players, and you can delete it later."
            onSelect={() => setStep("photo")}
          />
          <ChoiceCard
            emoji="🦩"
            title="Give me an animal"
            body="You get a character in the exact same art style — a flamingo, a manatee, an alligator. No photo, no camera, nothing sent anywhere."
            onSelect={() => setStep("animal")}
          />
          <Button variant="ghost" size="lg" onClick={() => setStep("details")} disabled={busy}>
            Back
          </Button>
        </>
      )}

      {step === "photo" && (
        <PhotoStep
          photo={photo}
          onPhoto={setPhoto}
          onBack={() => setStep("consent")}
          onError={setError}
          busy={busy}
          onSubmit={() => void save({ avatarConsent: true, photo })}
        />
      )}

      {step === "animal" && (
        <AnimalStep
          busy={busy}
          onBack={() => setStep("consent")}
          onError={setError}
          // §36.4 — choosing an animal withdraws any photo, and the server
          // deletes the stored object rather than only clearing the row.
          onSubmit={(animal) =>
            void save({ avatarConsent: false, photo: null, avatarAnimal: animal })
          }
        />
      )}
    </div>
  );
}
